import { Injectable } from '@angular/core';
import { TrackingRestService } from '../tracking-rest/tracking-rest.service';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import { PromotionModel } from 'src/app/model/promotion.model';
import { CreateSessionRequest, UpdateEventRequest, PaymentSelectionRequest, PendingPaymentRequest } from 'src/app/model/tracking-api.models';

@Injectable({
  providedIn: 'root',
})
export class ApiTrackingService {
  constructor(private trackingRestService: TrackingRestService) {}

  /**
   * Obtiene el UID de la sesión de tracking guardada en sessionStorage
   * @returns El UID de la sesión o null si no existe
   */
  getSessionUID(): string | null {
    const sessionTracking = sessionStorage.getItem('tracking-session');
    if (sessionTracking !== null) {
      try {
        const session = JSON.parse(sessionTracking);
        return session.uid || null;
      } catch (error) {
        console.error('Error al parsear tracking-session:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Obtiene el UID de la sesión de tracking o crea uno nuevo si no existe
   * Este método garantiza que siempre haya un UID disponible
   * @returns El UID de la sesión (existente o recién creado)
   */
  getOrCreateSessionUID(): string {
    // Intentar obtener UID existente
    const existingUID = this.getSessionUID();
    if (existingUID) {
      return existingUID;
    }

    // Si no existe, crear uno nuevo y guardarlo en sessionStorage
    const newUID = uuidv4();
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      try {
        const session = JSON.parse(sessionTracking);
        session.uid = newUID;
        sessionStorage.setItem('tracking-session', JSON.stringify(session));
      } catch (error) {
        console.error('Error al actualizar tracking-session con nuevo UID:', error);
        // Si hay error, crear una sesión mínima con el nuevo UID
        const minimalSession = {
          uid: newUID,
          initialDate: DateTime.now().toMillis(),
        };
        sessionStorage.setItem('tracking-session', JSON.stringify(minimalSession));
      }
    } else {
      // Crear una nueva sesión mínima si no existe
      const minimalSession = {
        uid: newUID,
        initialDate: DateTime.now().toMillis(),
      };
      sessionStorage.setItem('tracking-session', JSON.stringify(minimalSession));
    }

    return newUID;
  }

  /**
   * Crea un nuevo evento de tracking
   * @param cardNumber Numero de la tarjeta
   * @param enrollUser -1: Si no esta fidelizado, 0: Si esta fidelizado, 1: Si se fidelizo en el proceso
   * @param recovery Booleano que indica si el proceso es de recuperación de trackeo
   * @param lastStepReached Ultimo paso del proceso
   * @param email: Email del usuario
   */
  async createSession(cardNumber: string, enrollUser: { email: string }, recovery?: boolean, lastStepReached?: string): Promise<void> {
    try {
      const sessionTransaction = sessionStorage.getItem('tracking-session');
      let sessionUID: string = uuidv4();
      let isNewSession = true;

      // Verificar si hay una sesión anterior con la misma tarjeta
      if (sessionTransaction !== null) {
        try {
          const session = JSON.parse(sessionTransaction);
          if (session?.card && session.card.toString() === cardNumber.toString()) {
            const difference = DateTime.now().diff(DateTime.fromMillis(session.initialDate)).as('minutes');
            // Si la diferencia es menor a 30 minutos, reutilizar la sesión
            if (difference <= 30) {
              sessionUID = session.uid;
              isNewSession = false;
              console.log('Reutilizando sesión existente para tarjeta:', cardNumber);
              return; // Retornar sin llamar a la API
            }
          }
        } catch (parseError) {
          console.warn('Error al parsear sesión anterior, creando nueva:', parseError);
        }
      }

      // Solo crear nueva sesión si no hay una existente para la misma tarjeta
      if (isNewSession) {
        // Determinar el paso inicial
        const initialStep = recovery ? lastStepReached : 'PORTAL DE PROMOCIONES';

        // Llamar al nuevo endpoint REST en lugar de GraphQL
        const restRequest: CreateSessionRequest = {
          cardNumber: Number(cardNumber),
          email: enrollUser === null ? 'no-email@happyland.cl' : enrollUser.email,
          enrollUser: enrollUser,
          recovery: recovery,
          lastStepReached: initialStep,
        };

        const restResponse = await this.trackingRestService.createSession(restRequest);
        sessionUID = restResponse.uid;
      }

      // Guardar la sesión en sessionStorage
      const params = {
        uid: sessionUID,
        card: Number(cardNumber),
        initialDate: DateTime.now().toMillis(),
        finalDate: null,
        lastStep: recovery ? lastStepReached : 'PORTAL DE PROMOCIONES',
        paymentType: 'NINGUNO',
        fidelity: enrollUser === null ? 'SIN FIDELIZAR' : 'FIDELIZADO PREVIAMENTE',
        registeredEmail: enrollUser === null ? 'no-email@happyland.cl' : enrollUser.email,
        paymentAmount: 0,
        promotionCash: 0,
        promotionCashBonus: 0,
        promotionTickets: 0,
        promotionTokens: 0,
        promotionTokenBonus: 0,
      };

      sessionStorage.setItem('tracking-session', JSON.stringify(params));

      if (isNewSession) {
        console.log('Nueva sesión de tracking creada con UID:', sessionUID);
      }
    } catch (error) {
      console.error('Error en createSession:', error);
      // Mantener compatibilidad: si falla REST, no bloquear la aplicación
    }
  }

  /**
   * Actualiza el evento de tracking
   * @param lastStep Ultimo paso del proceso
   * @param paymentMethod  Método de pago
   * @param enrollStatus Booleano que indica si el usuario se fidelizo o no
   * @returns
   */
  async updateSessionOnlyStep(lastStep: string, amount?: number): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      try {
        const session = JSON.parse(sessionTracking);

        const params = {
          uid: session.uid,
          card: session.card,
          initialDate: session.initialDate,
          finalDate: DateTime.now().toMillis(),
          lastStep: lastStep,
          paymentType: session.paymentType,
          fidelity: session.fidelity,
          registeredEmail: session.registeredEmail,
          paymentAmount: amount ? amount : session.paymentAmount,
          promotionCash: session.promotionCash,
          promotionCashBonus: session.promotionCashBonus,
          promotionTickets: session.promotionTickets,
          promotionTokens: session.promotionTokens,
          promotionTokenBonus: session.promotionTokenBonus,
        };

        // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
        sessionStorage.setItem('tracking-session', JSON.stringify(params));

        // Llamar al nuevo endpoint REST con campos obligatorios
        if (session.uid) {
          const restRequest: UpdateEventRequest = {
            uid: session.uid,
            cardNumber: session.card,
            lastStepReached: lastStep,
            eventType: lastStep,
            eventData: { amount: amount },
            timestamp: DateTime.now().toMillis(),
          };

          await this.trackingRestService.updateSessionEvent(session.uid, restRequest);
        }

        return Promise.resolve();
      } catch (error) {
        console.error('Error en updateSessionOnlyStep:', error);
        // Mantener compatibilidad: si falla REST, no bloquear la aplicación
        return Promise.resolve();
      }
    }
  }

  /**
   * Actualiza el evento de tracking con información de pago
   * @param cardIdentification Identificación de tarjeta
   * @param lastStep Ultimo paso del proceso
   * @param paymentMethod  Método de pago
   * @param enrollStatus Booleano que indica si el usuario se fidelizo o no
   * @param promotion Información de promoción
   * @param email Email del usuario
   * @returns
   */
  async updateSessionStepWithPayment(
    cardIdentification: string,
    lastStep: string,
    paymentMethod: string,
    enrollStatus: boolean,
    promotion: PromotionModel,
    email?: string
  ): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      try {
        const session = JSON.parse(sessionTracking);

        const params = {
          uid: session.uid,
          card: session.card,
          initialDate: session.initialDate,
          finalDate: DateTime.now().toMillis(),
          lastStep: lastStep,
          paymentType: paymentMethod,
          fidelity: session.fidelity === 'SIN FIDELIZAR' ? (enrollStatus ? 'FIDELIZADO RECIENTEMENTE' : session.fidelity) : session.fidelity,
          registeredEmail: email || session.registeredEmail || 'no-email@happyland.cl',
          paymentAmount: promotion.amount,
          promotionCash: promotion.amount,
          promotionCashBonus: promotion.bonusAmount,
          promotionTickets: promotion.tickets,
          promotionTokens: promotion.token,
          promotionTokenBonus: promotion.bonusToken,
        };

        // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
        sessionStorage.setItem('tracking-session', JSON.stringify(params));

        // Llamar al nuevo endpoint REST con campos obligatorios
        if (session.uid) {
          const restRequest: PaymentSelectionRequest = {
            uid: session.uid,
            cardNumber: session.card,
            lastStepReached: lastStep,
            paymentMethod: paymentMethod,
            promotionData: {
              amount: promotion.amount,
              bonusAmount: promotion.bonusAmount,
              tickets: promotion.tickets,
              token: promotion.token,
              bonusToken: promotion.bonusToken,
            },
            enrollStatus: enrollStatus,
            email: email,
          };

          await this.trackingRestService.updatePaymentSelection(session.uid, restRequest);
        }

        return Promise.resolve();
      } catch (error) {
        console.error('Error en updateSessionStepWithPayment:', error);
        // Mantener compatibilidad: si falla REST, no bloquear la aplicación
        return Promise.resolve();
      }
    } else {
      this.createSession(cardIdentification, null, true, lastStep);
    }
  }

  /**
   * Finaliza el evento de tracking
   */
  async finalizeSession(lastStep: string): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      try {
        const session = JSON.parse(sessionTracking);

        const params = {
          uid: session.uid,
          card: session.card,
          initialDate: session.initialDate,
          finalDate: DateTime.now().toMillis(),
          lastStep: lastStep,
          paymentType: session.paymentType,
          fidelity: session.fidelity,
          registeredEmail: session.registeredEmail,
          paymentAmount: session.paymentAmount,
          promotionCash: session.promotionCash,
          promotionCashBonus: session.promotionCashBonus,
          promotionTickets: session.promotionTickets,
          promotionTokens: session.promotionTokens,
          promotionTokenBonus: session.promotionTokenBonus,
        };

        // Llamar al nuevo endpoint REST con campos obligatorios
        if (session.uid) {
          // Usar el endpoint genérico PUT con campos obligatorios + campos opcionales finales
          const restRequest: Record<string, unknown> = {
            uid: session.uid,
            cardNumber: session.card,
            lastStepReached: lastStep,
            finalDate: DateTime.now().toMillis(),
            paymentAmount: session.paymentAmount,
          };

          await this.trackingRestService.updateSession(session.uid, restRequest);
        }

        sessionStorage.removeItem('tracking-session');
        return Promise.resolve();
      } catch (error) {
        console.error('Error en finalizeSession:', error);
        // Mantener compatibilidad: si falla REST, no bloquear la aplicación
        sessionStorage.removeItem('tracking-session');
        return Promise.resolve();
      }
    }
  }
}
